import { get, isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import URI from 'urijs';
import { t, getKeyValue } from 'Src/i18n';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { appQueryParameters } from '@clubmed/utils';
import { renderInfoBanner } from 'Components/widgets/info-banner/infoBanner.utils';
import type { InfoBannerData } from 'Components/widgets/info-banner/infoBanner.query';
import 'Components/widgets/info-banner/infoBanner.query';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { selectProposal } from 'Store/booking/selected-proposal-id/selectedProposalId.action';
import { getResolvedProposals } from 'Utils/proposal/proposalUtils';
import {
  switchProposalsOffers,
  fetchRefreshProposal,
} from 'Store/booking/proposals/proposals.action';
import { getAlternativeFilteredAccommodations } from 'Store/booking/additional-accommodations/additionalAccommodations.action';
import { tAlt } from 'Components/utils/booking/bookingUtils';
import { OverlaidActionProvider } from 'Components/widgets/overlaid-action-provider/OverlaidActionProvider';
import { useAppContext } from 'Context/ReactAppContext';
import { BE_ACCOMMODATIONS_LAYER_NAME } from 'Views/booking/constants';
import { RefreshProposalModal } from 'Views/booking/refresh-proposal-modal/RefreshProposalModal';
import { SimpleNotification } from 'Components/frameworks/notification/SimpleNotification';
import { ICONS } from 'Components/utils/icon/icons';
import { useBookingNavbar } from 'Components/widgets/booking-navbar/useBookingNavbar';
import { AccommodationsTabs } from './AccommodationsTabs';
import { MoreRooms } from './more-rooms/MoreRooms';
import { getUniqDiscountDetails } from './proposal-accommodation-title/ProposalAccommodationTitleUtils';
type Props = {
  closeLayer: (arg0: boolean | null | undefined, arg1: string | null | undefined) => void;
  infoBannerData: InfoBannerData;
  openLayer: (arg0: string) => void;
  proposals: Array<Proposal>;
  resort: ProductWithAccommodations;
  selectedProposal: Proposal;
  streetViews: Array<StreetViewWithAccommodationCategoryId>;
};
export const AccommodationView = ({
  closeLayer,
  infoBannerData,
  openLayer,
  proposals,
  resort,
  selectedProposal,
  streetViews,
}: Props) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedRate, setSelectedRate] = React.useState(selectedProposal.price.selectedRate);
  const [proposal, setProposal] = React.useState(selectedProposal);
  const [refreshedProposal, setRefreshedProposal] = React.useState<RefreshedProposal>({
    proposal: null,
    errors: null,
  });
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const router = useRouter();
  const { trackClickEvent } = useTrackEvent();
  const showDisclamer = getKeyValue('booking.accommodation.promotion.showDisclamer');
  const uniqDiscountDetails = getUniqDiscountDetails(proposals);

  useBookingNavbar({ isPriceLoading: isLoading, noEdit: false });

  const getAlternativesForFilters = () => {
    const isAlternativeFiltersModeActive = isFeatureFlipped(appContext)(
      'booking.AlternativeFiltersModeActive',
    );

    if (isAlternativeFiltersModeActive && getAlternativeFilteredAccommodations) {
      dispatch(
        getAlternativeFilteredAccommodations({
          appContext,
          proposal: selectedProposal,
        }),
      );
    }
  };

  useEffect(() => {
    getAlternativesForFilters();
  }, []);
  useEffect(() => {
    if (
      proposal.id !== selectedProposal.id ||
      proposal.accommodations !== selectedProposal.accommodations
    ) {
      setProposal(selectedProposal);
      getAlternativesForFilters();
    }
  }, [selectedProposal]);

  const onMoreRoomsClickOpen = () => () => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION, {
      detail_click: 'see_more_rooms',
    });
    openLayer(BE_ACCOMMODATIONS_LAYER_NAME);
  };

  const onMoreRoomsSelect = (proposalId: string) => {
    const { hasFlexRates } = selectedProposal.price;
    closeLayer(!hasFlexRates, proposalId);
  };

  const onSelectProposal = async (proposalId: string) => {
    const updatedUri = new URI(router.asPath).setQuery(
      appQueryParameters.booking.selectedProposalId,
      proposalId,
    );
    router.replace(updatedUri.toString());
    dispatch(selectProposal(proposalId));
    const refreshProposal = await dispatch(fetchRefreshProposal(proposalId, undefined, appContext));
    return setRefreshedProposal(refreshProposal?.refreshProposal);
  };

  const switchOffers = (offers: Array<string>) => {
    setIsLoading(true);
    dispatch(switchProposalsOffers(offers, appContext)).finally(() => setIsLoading(false));
  };

  if (isEmpty(getResolvedProposals(proposals))) {
    return null;
  }

  const buttonProps = {
    label: tAlt({
      cruiseKeyPath: 'booking.accommodation.moreCabinsLabel',
      product: resort,
      resortKeyPath: 'booking.accommodation.moreRoomsLabel',
    }),
    trackingData: {
      appContext,
      baseProposal: proposals[0],
      selectedProposal: selectedProposal,
    },
  };
  const participantCount = get(selectedProposal, 'attendees.list.length', 0);
  const shouldDisplayMoreRoomsLayer =
    router.query[appQueryParameters.openLayer] === BE_ACCOMMODATIONS_LAYER_NAME;
  return (
    proposal && (
      <React.Fragment>
        {renderInfoBanner({
          infoBannerData,
          departureDate: selectedProposal.departureDate,
          arrivalDate: selectedProposal.arrivalDate,
          currentStep: 'accommodation',
        })}
        <OverlaidActionProvider
          button={{
            label: buttonProps.label,
            onClick: onMoreRoomsClickOpen(),
          }}
          dataSelector="MoreRooms"
          label={t('booking.accommodation.teasingMoreRooms')}
        >
          <AccommodationsTabs
            isLoading={isLoading}
            participantCount={participantCount}
            product={resort}
            proposals={proposals}
            selectedProposal={selectedProposal}
            selectedRate={selectedRate}
            selectProposal={onSelectProposal}
            setSelectedRate={setSelectedRate}
            switchOffers={switchOffers}
            streetViews={streetViews}
          />
        </OverlaidActionProvider>

        {showDisclamer && uniqDiscountDetails.length > 0 && (
          <SimpleNotification
            id="AccomodationDisclamer"
            title={t('booking.accommodation.promotion.disclamerTitle')}
            className="mt-40"
            content={
              <ul className="list-none">
                {uniqDiscountDetails.map(({ value: discount, description }, index) => (
                  <li key={`AccomodationDisclamer-${index * 1}`}>
                    {`${'*'.repeat(index + 1)}${t('booking.accommodation.promotion.disclamer', {
                      discount,
                    })}`}
                    {description && ` ${description}`}
                  </li>
                ))}
              </ul>
            }
            iconName={ICONS.EXCLAMATION_POINT}
          />
        )}
        {shouldDisplayMoreRoomsLayer && (
          <MoreRooms
            onClose={(proposalId) => closeLayer(false, proposalId)}
            onSubmit={onMoreRoomsSelect}
            proposal={selectedProposal}
            product={resort}
            streetViews={streetViews}
            switchOffers={switchOffers}
          />
        )}
        <RefreshProposalModal
          refreshedProposal={refreshedProposal}
          resort={resort}
          selectedProposal={selectedProposal}
        />
      </React.Fragment>
    )
  );
};
